import type { ReactNode } from 'react';
import { Box, CircularProgress } from '@mui/material';
import ProtectedRoute from '@/components/auth/ProtectedRoute';
import { usePermission } from '@/hooks/usePermission';
import { useBudgetScope } from './shared';

const FINANCE_PERMISSIONS = [
  'accounting.read', 'accounting.revenue.read', 'accounting.expense.read', 'accounting.reports.read',
  'budget.read', 'budget.write', 'budget.submit', 'budget.approve', 'budget.transfer', 'budget.admin',
  'budget.exec.read', 'budget.audit.export',
  'purchasing.read', 'purchasing.order', 'purchasing.approve',
];

/**
 * Guard for /finance. Anyone with a finance, budget or purchasing permission gets in, and so
 * does a department head — who holds no budget role; the backend derives their scope from
 * departments.head_of_department_id (viewer.head_of). Everyone else sees the Forbidden page.
 */
export default function FinanceAccessRoute({ children }: { children: ReactNode }) {
  const { hasAnyPermission } = usePermission();
  const allowed = hasAnyPermission(FINANCE_PERMISSIONS);
  const scope = useBudgetScope();
  if (allowed) return <ProtectedRoute>{children}</ProtectedRoute>;
  if (scope.loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>;
  }
  if (scope.headOf.length > 0) return <ProtectedRoute>{children}</ProtectedRoute>;
  return <ProtectedRoute requiredPermission={FINANCE_PERMISSIONS}>{children}</ProtectedRoute>;
}
